import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";

// =============== BLOG ===============
export function AdminBlog() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", body: "", author: "Studio Editorial", cover_image_url: "", tags: "", published: true });
  const load = () => api.get("/admin/blog").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blog", { ...form, tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Post created");
      setShow(false);
      load();
    } catch { toast.error("Failed"); }
  };
  const togglePublish = async (id, published) => { await api.patch(`/admin/blog/${id}`, { published: !published }); load(); };
  const remove = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/blog/${id}`); load(); } };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow text-[var(--crest-gold)]">— Insights</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">Blog Posts</h1>
        </div>
        <button onClick={() => setShow((v) => !v)} className="btn-primary" data-testid="blog-new"><Plus size={14} /> New Post</button>
      </div>
      {show && (
        <form onSubmit={create} className="admin-card grid md:grid-cols-2 gap-x-8 gap-y-2 mb-8">
          <input className="crest-input md:col-span-2" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input className="crest-input" placeholder="Slug (kebab-case)" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          <input className="crest-input" placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
          <input className="crest-input md:col-span-2" placeholder="Cover image URL" value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} />
          <input className="crest-input md:col-span-2" placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          <textarea className="crest-input md:col-span-2" placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          <textarea className="crest-input md:col-span-2" placeholder="Body (HTML allowed)" rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <button className="btn-primary md:col-span-2 mt-2">Publish</button>
        </form>
      )}
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p._id} className="admin-card flex items-center justify-between gap-4">
            <div>
              <div className="font-serif text-xl">{p.title}</div>
              <div className="text-xs text-[var(--crest-muted)] mt-1">/{p.slug} · {p.author} · {p.published ? "Published" : "Draft"}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => togglePublish(p._id, p.published)} className="text-xs gold uppercase tracking-widest flex items-center gap-1">
                {p.published ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
              </button>
              <button onClick={() => remove(p._id)} className="p-2 text-[var(--crest-muted)] hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== PRESS ===============
export function AdminPress() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", outlet: "", date: "", url: "", image_url: "", summary: "", published: true });
  const load = () => api.get("/press").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/press", form); toast.success("Added"); setShow(false); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/press/${id}`); load(); } };

  return (
    <div data-testid="admin-press-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow text-[var(--crest-gold)]">— Press</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">Press & Media</h1>
        </div>
        <button onClick={() => setShow((v) => !v)} className="btn-primary" data-testid="press-new"><Plus size={14} /> New Mention</button>
      </div>
      {show && (
        <form onSubmit={create} className="admin-card grid md:grid-cols-2 gap-x-8 gap-y-2 mb-8">
          <input className="crest-input md:col-span-2" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input className="crest-input" placeholder="Outlet" required value={form.outlet} onChange={(e) => setForm({ ...form, outlet: e.target.value })} />
          <input className="crest-input" placeholder="Date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <input className="crest-input md:col-span-2" placeholder="URL" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <textarea className="crest-input md:col-span-2" placeholder="Summary (optional)" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
          <button className="btn-primary md:col-span-2 mt-2">Save</button>
        </form>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((p) => (
          <div key={p._id} className="admin-card">
            <div className="text-xs uppercase tracking-widest gold">{p.outlet} · {p.date}</div>
            <div className="font-serif text-xl mt-2">{p.title}</div>
            {p.summary && <p className="text-sm text-[var(--crest-muted)] mt-2">{p.summary}</p>}
            <div className="flex items-center justify-between mt-3">
              <a href={p.url} target="_blank" rel="noreferrer" className="text-xs gold uppercase tracking-widest">View</a>
              <button onClick={() => remove(p._id)} className="text-[var(--crest-muted)] hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== FAQs ===============
export function AdminFAQs() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", sort_order: 0, published: true });
  const load = () => api.get("/faqs").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/faqs", form); toast.success("Added"); setForm({ question: "", answer: "", sort_order: 0, published: true }); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/admin/faqs/${id}`); load(); } };
  return (
    <div data-testid="admin-faqs-page">
      <div className="eyebrow text-[var(--crest-gold)]">— FAQs</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2 mb-8">Frequently Asked Questions</h1>
      <form onSubmit={create} className="admin-card mb-8 grid md:grid-cols-2 gap-x-8 gap-y-2">
        <input className="crest-input md:col-span-2" placeholder="Question" required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
        <textarea className="crest-input md:col-span-2" placeholder="Answer" required value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} />
        <input className="crest-input" type="number" placeholder="Sort order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
        <button className="btn-primary mt-4">Add FAQ</button>
      </form>
      <div className="space-y-3">
        {items.map((f) => (
          <div key={f._id} className="admin-card flex items-start justify-between gap-4">
            <div>
              <div className="font-serif text-lg">{f.question}</div>
              <div className="text-sm text-[var(--crest-muted)] mt-1">{f.answer}</div>
            </div>
            <button onClick={() => remove(f._id)} className="text-[var(--crest-muted)] hover:text-red-600 p-2"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== CREATORS ===============
export function AdminCreators() {
  const [apps, setApps] = useState([]);
  const [creators, setCreators] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", tiktok_handle: "", tiktok_url: "", niche: "", follower_count: "", photo_url: "", bio: "", featured: true, sort_order: 0 });
  const load = () => {
    api.get("/admin/creator-applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/creators").then((r) => setCreators(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const updateApp = async (id, status) => { await api.patch(`/admin/creator-applications/${id}`, { status }); load(); };
  const addFeatured = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/featured-creators", form); toast.success("Added"); setShow(false); load(); }
    catch { toast.error("Failed"); }
  };
  const removeFeatured = async (id) => { if (window.confirm("Remove?")) { await api.delete(`/admin/featured-creators/${id}`); load(); } };
  return (
    <div data-testid="admin-creators-page">
      <div className="eyebrow text-[var(--crest-gold)]">— Creator Network</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2 mb-8">TikTok Creator Network</h1>

      <div className="admin-card mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">Featured Creators ({creators.length})</h2>
          <button onClick={() => setShow((v) => !v)} className="btn-secondary"><Plus size={14} /> Add</button>
        </div>
        {show && (
          <form onSubmit={addFeatured} className="grid md:grid-cols-2 gap-x-8 gap-y-2 mb-4">
            <input className="crest-input" placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="crest-input" placeholder="@handle" required value={form.tiktok_handle} onChange={(e) => setForm({ ...form, tiktok_handle: e.target.value })} />
            <input className="crest-input" placeholder="TikTok URL" required value={form.tiktok_url} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} />
            <input className="crest-input" placeholder="Niche" required value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} />
            <input className="crest-input" placeholder="Follower count" value={form.follower_count} onChange={(e) => setForm({ ...form, follower_count: e.target.value })} />
            <input className="crest-input" placeholder="Photo URL" required value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} />
            <textarea className="crest-input md:col-span-2" placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            <button className="btn-primary md:col-span-2">Save</button>
          </form>
        )}
        <div className="grid md:grid-cols-3 gap-3">
          {creators.map((c) => (
            <div key={c._id} className="border border-black/10 p-3 flex gap-3">
              <img src={c.photo_url} alt="" className="w-16 h-16 object-cover" />
              <div className="flex-1">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-[var(--crest-muted)]">{c.tiktok_handle}</div>
              </div>
              <button onClick={() => removeFeatured(c._id)} className="text-[var(--crest-muted)] hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-card">
        <h2 className="font-serif text-2xl mb-4">Applications ({apps.length})</h2>
        <div className="space-y-3">
          {apps.length === 0 && <div className="text-sm text-[var(--crest-muted)]">No applications yet.</div>}
          {apps.map((a) => (
            <div key={a._id} className="border border-black/10 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{a.full_name} · <a href={`https://www.tiktok.com/${a.tiktok_handle}`} target="_blank" rel="noreferrer" className="gold">{a.tiktok_handle}</a></div>
                  <div className="text-xs text-[var(--crest-muted)]">{a.email}{a.phone ? ` · ${a.phone}` : ""} · {a.follower_count} · {a.niche}</div>
                </div>
                <select value={a.status} onChange={(e) => updateApp(a._id, e.target.value)} className="crest-input text-xs">
                  {["applied", "reviewing", "approved", "declined"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <p className="text-sm mt-2 text-[var(--crest-muted)]">{a.why_join}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============== ANNOUNCEMENTS ===============
export function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", cta_label: "", cta_url: "", active: true, style: "banner" });
  const load = () => api.get("/admin/announcements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/announcements", form); toast.success("Posted"); setForm({ title: "", message: "", cta_label: "", cta_url: "", active: true, style: "banner" }); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => { await api.delete(`/admin/announcements/${id}`); load(); };
  return (
    <div data-testid="admin-announcements-page">
      <div className="eyebrow text-[var(--crest-gold)]">— Announcements</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2 mb-8">Site Banner</h1>
      <form onSubmit={create} className="admin-card grid md:grid-cols-2 gap-x-8 gap-y-2 mb-8">
        <input className="crest-input md:col-span-2" placeholder="Headline" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <textarea className="crest-input md:col-span-2" placeholder="Message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        <input className="crest-input" placeholder="CTA label (optional)" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} />
        <input className="crest-input" placeholder="CTA url (optional)" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} />
        <button className="btn-primary md:col-span-2 mt-2">Publish Announcement</button>
      </form>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a._id} className="admin-card flex items-start justify-between">
            <div>
              <div className="font-medium">{a.title}</div>
              <div className="text-sm text-[var(--crest-muted)] mt-1">{a.message}</div>
              <div className="text-xs mt-2 gold uppercase tracking-widest">{a.active ? "Active" : "Inactive"}</div>
            </div>
            <button onClick={() => remove(a._id)} className="text-[var(--crest-muted)] hover:text-red-600"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== TEAM ===============
export function AdminTeam() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const load = () => api.get("/auth/admins").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/admins", form); toast.success("Admin added"); setShow(false); setForm({ name: "", email: "", password: "", role: "admin" }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const updateRole = async (id, role) => { await api.patch(`/auth/admins/${id}`, { role }); load(); };
  const toggleActive = async (id, active) => { await api.patch(`/auth/admins/${id}`, { active: !active }); load(); };
  const remove = async (id) => { if (window.confirm("Delete admin?")) { await api.delete(`/auth/admins/${id}`); load(); } };

  return (
    <div data-testid="admin-team-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow text-[var(--crest-gold)]">— Team</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">Studio Admins</h1>
        </div>
        <button onClick={() => setShow((v) => !v)} className="btn-primary" data-testid="team-new"><Plus size={14} /> Add Admin</button>
      </div>
      {show && (
        <form onSubmit={create} className="admin-card grid md:grid-cols-2 gap-x-8 gap-y-2 mb-8">
          <input className="crest-input" placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-form-name" />
          <input type="email" className="crest-input" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-form-email" />
          <input type="password" minLength={8} className="crest-input" placeholder="Password (8+ chars)" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-form-password" />
          <select className="crest-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-form-role">
            <option>admin</option>
            <option>superadmin</option>
          </select>
          <button className="btn-primary md:col-span-2 mt-4" data-testid="team-form-submit">Create Admin</button>
        </form>
      )}
      <div className="admin-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[var(--crest-muted)] border-b border-black/10"><th className="py-3 px-2">Name</th><th className="py-3 px-2">Email</th><th className="py-3 px-2">Role</th><th className="py-3 px-2">Active</th><th className="py-3 px-2 text-right">Actions</th></tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-black/5" data-testid={`team-row-${a.id}`}>
                <td className="py-3 px-2">{a.name}</td>
                <td className="py-3 px-2 text-[var(--crest-muted)]">{a.email}</td>
                <td className="py-3 px-2">
                  <select value={a.role} onChange={(e) => updateRole(a.id, e.target.value)} className="crest-input text-xs">
                    <option>admin</option><option>superadmin</option>
                  </select>
                </td>
                <td className="py-3 px-2">
                  <button onClick={() => toggleActive(a.id, a.active)} className="text-xs gold uppercase tracking-widest">{a.active ? "Active" : "Inactive"}</button>
                </td>
                <td className="py-3 px-2 text-right">
                  <button onClick={() => remove(a.id)} className="text-[var(--crest-muted)] hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
